import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { MailAttToPraticaDialog } from "../../components/MailAttToPraticaDialog";
import {
  Mail, ShieldCheck, Inbox, Send, FileEdit, Trash2, RefreshCw, Plus, Loader2, Paperclip,
  ArrowLeft, X, Download, Reply, RotateCcw, Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  FolderKanban, Search,
  Unlink, Pencil, CheckCheck, MailOpen,
} from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import LetterComposer from "../../components/LetterComposer";
import { useStoredWidth, ResizeHandle } from "../../components/ResizeHandle";

const FOLDERS = [
  { key: "inbox", label: "In arrivo", icon: Inbox },
  { key: "sent", label: "Inviati", icon: Send },
  { key: "drafts", label: "Bozze", icon: FileEdit },
  { key: "trash", label: "Cestino", icon: Trash2 },
];

const fmtBytes = (n) => {
  if (!n) return "";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
};

const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });

function BodyEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: "prose prose-invert max-w-none min-h-[200px] focus:outline-none px-3 py-2 text-slate-100" } },
  });
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);
  if (!editor) return null;
  const Btn = ({ active, onClick, children, tid }) => (
    <button type="button" onClick={onClick} data-testid={tid}
      className={`h-8 w-8 flex items-center justify-center rounded transition-colors ${active ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}>{children}</button>
  );
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden">
      <div className="flex items-center gap-1 border-b border-slate-800 px-2 py-1.5">
        <Btn tid="mail-ed-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn tid="mail-ed-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn tid="mail-ed-underline" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>
        <div className="w-px h-5 bg-slate-700 mx-1" />
        <Btn tid="mail-ed-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn tid="mail-ed-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export function ComposeDialog({ open, onClose, account, initial, onSent, praticaId }) {
  const isPec = account === "pec";
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [ccn, setCcn] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [atts, setAtts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [studio, setStudio] = useState(null);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const i = initial || {};
    setTo((i.to || []).join(", "));
    setCc((i.cc || []).join(", "));
    setCcn((i.bcc || []).join(", "));
    setSubject(i.subject || "");
    setBody(i.html || "");
    setAtts(i.attachments || []);
    setDraftId(i.draftId || null);
  }, [open, initial]);

  useEffect(() => {
    if (open && !studio) api.get("/studio/me").then(({ data }) => setStudio(data)).catch(() => {});
  }, [open, studio]);

  const parseList = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

  const addFiles = async (files) => {
    const list = [];
    for (const f of files) {
      const b64 = await fileToB64(f);
      list.push({ nome: f.name, mime: f.type || "application/octet-stream", size: f.size, content_b64: b64 });
    }
    setAtts((a) => [...a, ...list]);
  };

  const payload = () => ({
    to: parseList(to), cc: parseList(cc), bcc: parseList(ccn),
    subject: subject.trim(), html: body || "", attachments: atts,
  });

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const p = payload();
      if (draftId) await api.patch(`/studio/mail/${account}/drafts/${draftId}`, p);
      else { const { data } = await api.post(`/studio/mail/${account}/drafts`, p); setDraftId(data.id); }
      toast.success("Bozza salvata");
      onSent("drafts");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingDraft(false); }
  };

  const send = async () => {
    const p = payload();
    if (!p.to.length) { toast.error("Inserisci almeno un destinatario valido"); return; }
    if (!p.subject) { toast.error("Inserisci l'oggetto"); return; }
    setSending(true);
    try {
      await api.post(`/studio/mail/${account}/send`, { ...p, draft_id: draftId, pratica_id: praticaId || undefined });
      toast.success(isPec ? "PEC inviata" : "Email inviata");
      onClose();
      onSent("sent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="mail-compose-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            {isPec ? <ShieldCheck className="h-5 w-5 text-amber-400" /> : <Mail className="h-5 w-5 text-amber-400" />}
            {isPec ? "Nuova PEC" : "Nuova email"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <Label className="text-slate-300 text-sm">A</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="destinatario@email.it, altro@email.it" data-testid="mail-compose-to" className="mt-1 bg-slate-900/60 border-slate-700" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-sm">CC</Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} data-testid="mail-compose-cc" className="mt-1 bg-slate-900/60 border-slate-700" />
            </div>
            <div>
              <Label className="text-slate-300 text-sm">CCN</Label>
              <Input value={ccn} onChange={(e) => setCcn(e.target.value)} data-testid="mail-compose-ccn" className="mt-1 bg-slate-900/60 border-slate-700" />
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Oggetto</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="mail-compose-subject" className="mt-1 bg-slate-900/60 border-slate-700" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm">Contenuto</Label>
            <div className="mt-1">
              <LetterComposer studio={studio} value={body} onChange={setBody} fileName={`${(subject || "documento").replace(/[^\p{L}\p{N}\-_]+/gu, "_")}.pdf`} />
            </div>
          </div>
          <div>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addFiles(Array.from(e.target.files || [])); e.target.value = ""; }} data-testid="mail-compose-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="mail-compose-attach" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
              <Paperclip className="h-4 w-4 mr-1" /> Allega file
            </Button>
            {atts.length > 0 && (
              <div className="mt-2 space-y-1">
                {atts.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-300 bg-slate-900/40 rounded px-3 py-1.5" data-testid={`mail-compose-att-${i}`}>
                    <Paperclip className="h-3.5 w-3.5 text-amber-400" />
                    <span className="truncate flex-1">{a.nome}</span>
                    <span className="text-xs text-slate-500 font-mono">{fmtBytes(a.size)}</span>
                    <button onClick={() => setAtts((x) => x.filter((_, j) => j !== i))} data-testid={`mail-compose-att-del-${i}`} className="text-red-400 hover:text-red-300"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={saveDraft} disabled={savingDraft || sending} data-testid="mail-compose-save-draft" className="border-slate-700 text-slate-200 hover:bg-slate-800">
              {savingDraft ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <FileEdit className="h-4 w-4 mr-1" />} Salva bozza
            </Button>
            <Button onClick={send} disabled={sending} data-testid="mail-compose-send" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Invia
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MailClient({ account }) {
  const [sideW, setSideW] = useStoredWidth("pgp_w_mail", 210, 160, 400);
  const isPec = account === "pec";
  const [status, setStatus] = useState(null);
  const [folder, setFolder] = useState("inbox");
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeInit, setComposeInit] = useState(null);
  const [storage, setStorage] = useState(null);
  const [checked, setChecked] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setChecked([]); }, [folder, account]);
  const toggleCheck = (id) => setChecked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const markAllRead = async () => {
    try {
      await api.post(`/studio/mail/${account}/mark-all-read`);
      setUnread(0);
      setMessages((x) => x.map((i) => ({ ...i, read: true })));
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
      toast.success("Tutti i messaggi segnati come letti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadStorage = useCallback(async () => {
    try {
      const { data } = await api.get(`/studio/mail/${account}/storage`);
      setStorage(data);
    } catch { setStorage(null); }
  }, [account]);

  const load = useCallback(async (fld = folder) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/studio/mail/${account}/messages`, { params: { folder: fld } });
      setMessages(data.messages || []);
      setUnread(data.unread_inbox || 0);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [account, folder]);

  useEffect(() => {
    setSelected(null);
    api.get(`/studio/mail/${account}/status`).then(({ data }) => setStatus(data)).catch(() => setStatus({ configured: false }));
    load("inbox");
    setFolder("inbox");
    loadStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const sync = useCallback(async (silent = false) => {
    if (!silent) setSyncing(true);
    try {
      const { data } = await api.post(`/studio/mail/${account}/sync`);
      if (data.new > 0) {
        window.dispatchEvent(new Event("pgp-mail-unread-changed"));
        toast.success(`${data.new} ${data.new === 1 ? "nuovo messaggio" : "nuovi messaggi"} in arrivo`);
        if (folder === "inbox") load("inbox");
        else { const { data: inb } = await api.get(`/studio/mail/${account}/messages`, { params: { folder: "inbox" } }); setUnread(inb.unread_inbox || 0); }
      } else if (!silent) {
        if (!data.imap) toast.info("IMAP non configurato: aggiungi il server IMAP in Impostazioni Studio → Configurazione Email.");
        else toast.info("Nessun nuovo messaggio");
      }
      loadStorage();
    } catch (e) { if (!silent) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { if (!silent) setSyncing(false); }
  }, [account, folder, load, loadStorage]);

  // Auto-refresh ogni 5 minuti
  useEffect(() => {
    const id = setInterval(() => sync(true), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [sync]);

  const openFolder = (f) => { setFolder(f); setSelected(null); load(f); };

  const navigate = useNavigate();
  const [linkMsg, setLinkMsg] = useState(null);
  const [attMsg, setAttMsg] = useState(null);
  const [linkQuery, setLinkQuery] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkStep, setLinkStep] = useState("pratica"); // "pratica" | "soggetto"
  const [linkPratica, setLinkPratica] = useState(null); // pratica selezionata quando ci sono più soggetti
  const [linkSoggetti, setLinkSoggetti] = useState([]);
  const normName = (s) => (s || "").trim().toLowerCase();
  // Converte una data ISO (AAAA-MM-GG) in formato italiano GG/MM/AAAA; lascia invariate le stringhe già formattate.
  const itDate = (s) => {
    if (!s) return s;
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  const openLinkDialog = (m) => { setLinkMsg(m); setLinkQuery(""); setLinkResults([]); setLinkOpen(false); setLinkStep("pratica"); setLinkPratica(null); setLinkSoggetti([]); };
  const searchLinkPratica = async (q) => {
    setLinkQuery(q);
    if (!q || q.trim().length < 2) { setLinkResults([]); setLinkOpen(false); return; }
    try {
      const { data } = await api.get(`/studio/pratiche-search?q=${encodeURIComponent(q.trim())}`);
      setLinkResults(data || []);
      setLinkOpen(true);
    } catch { setLinkResults([]); }
  };
  const doLink = async (r, soggetto) => {
    const payload = { pratica_id: r.id, numero_pratica: r.numero_pratica, ...(soggetto ? { soggetto } : {}) };
    if (linkMsg.bulk) {
      for (const id of linkMsg.bulk) await api.patch(`/studio/mail/${account}/messages/${id}/pratica`, payload);
      toast.success(`${linkMsg.bulk.length} messaggi collegati alla pratica ${r.numero_pratica || ""}`.trim());
      setChecked([]);
    } else {
      await api.patch(`/studio/mail/${linkMsg.account || account}/messages/${linkMsg.id}/pratica`, payload);
      toast.success(soggetto ? `Email collegata a ${soggetto}` : "Messaggio collegato alla pratica");
    }
    window.dispatchEvent(new Event("pgp-mail-link-changed"));
    setLinkMsg(null);
    load(folder);
  };
  const unlinkMail = async (m) => {
    if (!window.confirm(`Scollegare questa email dalla pratica ${m.pratica_numero}? La nota "Email ricevuta" verrà tolta dal Diario della pratica.`)) return;
    try {
      await api.delete(`/studio/mail/${m.account || account}/messages/${m.id}/pratica`);
      toast.success("Email scollegata dalla pratica");
      window.dispatchEvent(new Event("pgp-mail-link-changed"));
      load(folder);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmLinkPratica = async (r) => {
    if (!linkMsg) return;
    setLinkSaving(true);
    try {
      const { data } = await api.get(`/studio/pratiche/${r.id}/soggetti`);
      const soggetti = data || [];
      if (soggetti.length > 1) {
        setLinkPratica(r);
        setLinkSoggetti(soggetti);
        setLinkStep("soggetto");
        setLinkOpen(false);
      } else {
        await doLink(r);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLinkSaving(false); }
  };
  const confirmLinkSoggetto = async (sog) => {
    if (!linkPratica) return;
    setLinkSaving(true);
    try {
      await doLink(linkPratica, sog.ruolo ? `${sog.nome} (${sog.ruolo})` : sog.nome);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLinkSaving(false); }
  };
  const confirmSuggestion = async (m, r) => {
    try {
      await api.patch(`/studio/mail/${account}/messages/${m.id}/pratica`, { pratica_id: r.id, numero_pratica: r.numero_pratica });
      toast.success("Email collegata alla pratica");
      window.dispatchEvent(new Event("pgp-mail-link-changed"));
      load(folder);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const dismissSuggestion = async (m) => {
    try {
      await api.patch(`/studio/mail/${account}/messages/${m.id}/suggestion/dismiss`);
      setMessages((x) => x.map((i) => (i.id === m.id ? { ...i, suggestion_dismissed: true } : i)));
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openMessage = async (m) => {
    try {
      const { data } = await api.get(`/studio/mail/${account}/messages/${m.id}`);
      setSelected(data);
      if (folder === "inbox" && !m.read) { setMessages((x) => x.map((i) => (i.id === m.id ? { ...i, read: true } : i))); setUnread((u) => Math.max(0, u - 1)); window.dispatchEvent(new Event("pgp-mail-unread-changed")); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCompose = (init = null) => { setComposeInit(init); setComposeOpen(true); };

  const editDraft = (m) => openCompose({ to: m.to, cc: m.cc, bcc: m.bcc, subject: m.subject, html: m.html, attachments: m.attachments, draftId: m.id });

  const trashMsg = async (m) => {
    try {
      const { data } = await api.post(`/studio/mail/${account}/messages/${m.id}/trash`);
      toast.success(data.folder === "trash" ? "Spostato nel cestino" : "Ripristinato");
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
      setSelected(null); load(folder);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteMsg = async (m) => {
    try {
      await api.delete(`/studio/mail/${account}/messages/${m.id}`);
      toast.success("Eliminato definitivamente");
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
      setSelected(null); load(folder);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkRead = async (read) => {
    try {
      const { data } = await api.post(`/studio/mail/${account}/bulk-read`, { ids: checked, read });
      setMessages((x) => x.map((i) => (checked.includes(i.id) ? { ...i, read } : i)));
      setUnread(data.unread_inbox || 0);
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
      toast.success(read ? "Segnati come letti" : "Segnati come non letti");
      setChecked([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/studio/mail/${account}/bulk-delete`, { ids: checked });
      toast.success(data.deleted ? `${data.deleted} eliminati definitivamente` : `${data.trashed} spostati nel cestino`);
      setChecked([]); setConfirmBulk(false); load(folder);
      window.dispatchEvent(new Event("pgp-mail-unread-changed"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const downloadAtt = (a) => {
    try {
      const bin = atob(a.content_b64 || "");
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: a.mime || "application/octet-stream" }));
      const link = document.createElement("a"); link.href = url; link.download = a.nome || "allegato"; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch { toast.error("Download non riuscito"); }
  };

  const Title = isPec ? "PEC" : "Email";
  const HeaderIcon = isPec ? ShieldCheck : Mail;
  const notConfigured = status && !status.configured;

  const listPeople = (m) => folder === "sent" || folder === "drafts"
    ? `A: ${(m.to || []).join(", ") || "—"}`
    : (m.from_name || m.from_addr || "—");

  return (
    <div className="p-6" data-testid={`mail-page-${account}`}>
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <HeaderIcon className="h-5 w-5 text-amber-400" />
        </div>
        <div>
          <h1 className="font-head font-bold text-2xl text-slate-50">{Title}</h1>
          <p className="text-sm text-slate-400">Client di posta {isPec ? "certificata" : "elettronica"} dello studio{status?.from ? ` · ${status.from}` : ""}</p>
        </div>
      </div>

      {notConfigured && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300 mb-4" data-testid="mail-not-configured">
          {isPec ? "PEC" : "Email"} non configurata. Il Titolare deve impostarla in <strong>Impostazioni Studio → {isPec ? "Configurazione PEC" : "Configurazione Email"}</strong>.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[var(--side-w)_minmax(0,1fr)] gap-4" style={{ "--side-w": `${sideW}px` }}>
        {/* Folder rail */}
        <div className="relative pgp-card p-3 h-fit">
          <ResizeHandle width={sideW} onChange={setSideW} testId="mail-side-resize-handle" className="hidden lg:block" />
          <Button onClick={() => openCompose()} data-testid="mail-new-button" className="w-full bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold mb-3">
            <Plus className="h-4 w-4 mr-1" /> {isPec ? "Nuova PEC" : "Nuova email"}
          </Button>
          <nav className="space-y-1">
            {FOLDERS.map((f) => {
              const FIcon = f.icon;
              return (
                <button key={f.key} onClick={() => openFolder(f.key)} data-testid={`mail-folder-${f.key}`}
                  className={`w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${folder === f.key ? "bg-amber-500/15 text-amber-300" : "text-slate-300 hover:bg-slate-800/50"}`}>
                  <FIcon className="h-4 w-4" />
                  <span className="flex-1 text-left">{f.label}</span>
                  {f.key === "inbox" && unread > 0 && <span className="text-[11px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5" data-testid="mail-unread-badge">{unread}</span>}
                </button>
              );
            })}
          </nav>
          <Button variant="outline" onClick={() => sync(false)} disabled={syncing} data-testid="mail-sync-button" className="w-full mt-3 border-slate-700 text-slate-200 hover:bg-slate-800 h-8">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />} Aggiorna posta
          </Button>
          {storage && storage.supported && (() => {
            const fmtSize = (b) => {
              if (b >= 1024 ** 3) return (b / 1024 ** 3).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + " GB";
              if (b >= 1024 ** 2) return (b / 1024 ** 2).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " MB";
              if (b >= 1024) return (b / 1024).toLocaleString("it-IT", { maximumFractionDigits: 0 }) + " KB";
              return `${b} B`;
            };
            const pct = storage.limit > 0 ? (storage.used / storage.limit) * 100 : 0;
            const color = pct > 90 ? "#ef4444" : pct > 70 ? "#f59e0b" : "#22c55e";
            return (
              <div className="mt-3 pt-3 border-t border-slate-800" data-testid="mail-storage-indicator">
                <div className="text-[11px] text-slate-400 mb-1.5">
                  Spazio usato: <span className="text-slate-200 font-medium" data-testid="mail-storage-text">{fmtSize(storage.used)} / {fmtSize(storage.limit)}</span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, backgroundColor: color }} data-testid="mail-storage-bar" />
                </div>
              </div>
            );
          })()}
        </div>

        {/* List / viewer */}
        <div className="pgp-card overflow-hidden min-h-[60vh]">
          {selected ? (
            <div data-testid="mail-viewer">
              <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-3">
                <button onClick={() => setSelected(null)} data-testid="mail-viewer-back" className="flex items-center gap-1 text-sm text-slate-400 hover:text-amber-400"><ArrowLeft className="h-4 w-4" /> Indietro</button>
                <div className="flex-1" />
                {folder === "drafts" && <Button size="sm" variant="outline" onClick={() => editDraft(selected)} data-testid="mail-viewer-edit-draft" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8"><FileEdit className="h-4 w-4 mr-1" /> Modifica</Button>}
                {(folder === "inbox") && <Button size="sm" variant="outline" onClick={() => openCompose({ to: [selected.from_addr], subject: `Re: ${selected.subject}` })} data-testid="mail-viewer-reply" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8"><Reply className="h-4 w-4 mr-1" /> Rispondi</Button>}
                {folder === "trash"
                  ? (<>
                      <Button size="sm" variant="outline" onClick={() => trashMsg(selected)} data-testid="mail-viewer-restore" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8"><RotateCcw className="h-4 w-4 mr-1" /> Ripristina</Button>
                      <Button size="sm" variant="outline" onClick={() => deleteMsg(selected)} data-testid="mail-viewer-delete" className="border-red-500/40 text-red-300 hover:bg-red-500/10 h-8"><Trash2 className="h-4 w-4 mr-1" /> Elimina</Button>
                    </>)
                  : (<Button size="sm" variant="outline" onClick={() => trashMsg(selected)} data-testid="mail-viewer-trash" className="border-slate-700 text-slate-200 hover:bg-slate-800 h-8"><Trash2 className="h-4 w-4 mr-1" /> Cestino</Button>)}
              </div>
              <div className="px-5 py-4">
                <h2 className="font-head font-semibold text-lg text-slate-100" data-testid="mail-viewer-subject">{selected.subject || "(nessun oggetto)"}</h2>
                <div className="mt-2 text-sm text-slate-400 space-y-0.5">
                  <div><span className="text-slate-500">Da:</span> {selected.from_name ? `${selected.from_name} · ` : ""}{selected.from_addr || "—"}</div>
                  <div><span className="text-slate-500">A:</span> {(selected.to || []).join(", ") || "—"}</div>
                  {(selected.cc || []).length > 0 && <div><span className="text-slate-500">CC:</span> {selected.cc.join(", ")}</div>}
                  <div><span className="text-slate-500">Data:</span> {formatDateTime(selected.date)}</div>
                </div>
                {(selected.attachments || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {selected.folder === "inbox" && (
                      <button onClick={() => (selected.pratica_id ? setAttMsg(selected) : (toast.info("Collega prima l'email a una pratica"), openLinkDialog(selected)))} data-testid="mail-viewer-att-to-pratica"
                        className="flex items-center gap-1.5 text-xs font-semibold text-[#0a0d14] bg-amber-500 hover:bg-amber-600 rounded px-2.5 py-1.5 transition-colors">
                        <FolderKanban className="h-3.5 w-3.5" /> Salva negli allegati pratica
                      </button>
                    )}
                    {selected.attachments.map((a, i) => (
                      <button key={i} onClick={() => downloadAtt(a)} data-testid={`mail-viewer-att-${i}`} className="flex items-center gap-2 text-xs text-slate-200 bg-slate-900/60 border border-slate-700 rounded px-2.5 py-1.5 hover:border-amber-500">
                        <Paperclip className="h-3.5 w-3.5 text-amber-400" /> <span className="max-w-[180px] truncate">{a.nome}</span> <Download className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                    ))}
                  </div>
                )}
                <div className="mt-4 border-t border-slate-800 pt-4 text-slate-200 text-sm leading-relaxed [&_a]:text-amber-400" data-testid="mail-viewer-body" dangerouslySetInnerHTML={{ __html: selected.html || "" }} />
              </div>
            </div>
          ) : (
            <div data-testid="mail-list">
              {!loading && messages.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-slate-800 bg-slate-900/40" data-testid="mail-bulk-toolbar">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input type="checkbox" className="accent-amber-500 h-4 w-4" data-testid="mail-select-all"
                      checked={checked.length > 0 && checked.length === messages.length}
                      onChange={(e) => setChecked(e.target.checked ? messages.map((m) => m.id) : [])} />
                    {checked.length ? `${checked.length} selezionati` : "Seleziona tutti"}
                  </label>
                  <div className="ml-auto flex items-center gap-2">
                    {checked.length > 0 && (<>
                      <Button size="sm" variant="outline" onClick={() => bulkRead(true)} data-testid="mail-bulk-read"
                        className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800"><MailOpen className="h-4 w-4 mr-1" /> Segna come letti</Button>
                      <Button size="sm" variant="outline" onClick={() => bulkRead(false)} data-testid="mail-bulk-unread"
                        className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800"><Mail className="h-4 w-4 mr-1" /> Segna come non letti</Button>
                      {folder !== "drafts" && (
                        <Button size="sm" variant="outline" onClick={() => openLinkDialog({ bulk: [...checked] })} data-testid="mail-bulk-link"
                          className="h-8 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"><FolderKanban className="h-4 w-4 mr-1" /> Collega a pratica</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setConfirmBulk(true)} data-testid="mail-bulk-delete"
                        className="h-8 border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 className="h-4 w-4 mr-1" /> Elimina selezionate ({checked.length})</Button>
                    </>)}
                    {folder === "inbox" && (
                      <Button size="sm" variant="outline" onClick={markAllRead} disabled={unread === 0} data-testid="mail-mark-all-read"
                        className="h-8 border-slate-700 text-slate-200 hover:bg-slate-800"><CheckCheck className="h-4 w-4 mr-1" /> Segna tutto come letto</Button>
                    )}
                  </div>
                </div>
              )}
              {loading ? (
                <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
              ) : messages.length === 0 ? (
                <div className="p-12 text-center text-sm text-slate-500" data-testid="mail-list-empty">Nessun messaggio in questa cartella.</div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {messages.map((m) => (
                    <li key={m.id} className="flex items-stretch">
                      <label className="flex items-start pl-4 pt-3.5 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="accent-amber-500 h-4 w-4" checked={checked.includes(m.id)} onChange={() => toggleCheck(m.id)} data-testid={`mail-check-${m.id}`} />
                      </label>
                      <button onClick={() => openMessage(m)} data-testid={`mail-msg-${m.id}`}
                        className={`flex-1 min-w-0 text-left px-4 py-3 hover:bg-slate-800/40 transition-colors flex items-start gap-3 ${!m.read && folder === "inbox" ? "bg-amber-500/[0.04]" : ""}`}>
                        {!m.read && folder === "inbox" && <span className="mt-1.5 h-2 w-2 rounded-full bg-amber-500 shrink-0" data-testid={`mail-msg-unread-${m.id}`} />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`truncate text-sm ${!m.read && folder === "inbox" ? "font-semibold text-slate-100" : "text-slate-300"}`}>{listPeople(m)}</span>
                            {m.has_attachments && <Paperclip className="h-3 w-3 text-slate-500 shrink-0" />}
                            <span className="ml-auto text-[11px] text-slate-500 font-mono shrink-0">{formatDateTime(m.date)}</span>
                          </div>
                          <div className={`truncate text-sm ${!m.read && folder === "inbox" ? "text-slate-200" : "text-slate-400"}`}>{m.subject || "(nessun oggetto)"}</div>
                          {m.snippet && <div className="truncate text-xs text-slate-500 mt-0.5">{m.snippet}</div>}
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] font-mono text-slate-500">{folder === "sent" ? "→ Inviata" : folder === "inbox" ? "← Ricevuta" : ""}</span>
                            {m.pratica_numero ? (
                              <span className="inline-flex items-center gap-1">
                              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); navigate(m.pratica_id ? `/app/pratiche?p=${m.pratica_id}` : "/app/pratiche"); }}
                                data-testid={`mail-pratica-badge-${m.id}`}
                                className="text-[10px] font-mono bg-cyan-500/15 text-cyan-300 rounded px-1.5 py-0.5 hover:bg-cyan-500/25 cursor-pointer">
                                PRATICA {m.pratica_numero}
                              </span>
                              {folder === "inbox" && (<>
                                <span role="button" tabIndex={0} title="Cambia pratica collegata" onClick={(e) => { e.stopPropagation(); openLinkDialog(m); }}
                                  data-testid={`mail-relink-pratica-${m.id}`} className="p-0.5 rounded text-slate-500 hover:text-amber-400 cursor-pointer transition-colors"><Pencil className="h-3 w-3" /></span>
                                <span role="button" tabIndex={0} title="Scollega dalla pratica" onClick={(e) => { e.stopPropagation(); unlinkMail(m); }}
                                  data-testid={`mail-unlink-pratica-${m.id}`} className="p-0.5 rounded text-slate-500 hover:text-red-400 cursor-pointer transition-colors"><Unlink className="h-3 w-3" /></span>
                              </>)}
                              </span>
                            ) : folder === "inbox" ? (
                              <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); openLinkDialog(m); }}
                                data-testid={`mail-link-pratica-${m.id}`}
                                className="text-[10px] text-amber-400 hover:underline cursor-pointer">
                                + Collega a pratica
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </button>
                      {folder === "inbox" && !m.pratica_id && (m.pratiche_suggerite || []).length > 0 && !m.suggestion_dismissed && (
                        <div className="px-4 pb-3 pt-2 bg-amber-400 border-t border-amber-500" data-testid={`mail-suggest-${m.id}`}>
                          {m.pratiche_suggerite.length === 1 ? (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
                              <span className="text-slate-900">Questa email sembra appartenere a <strong className="text-slate-900 font-bold">{m.pratiche_suggerite[0].nome}</strong> — Pratica <span className="font-mono font-bold text-slate-900">{m.pratiche_suggerite[0].numero_pratica}</span>. Confermi?</span>
                              <div className="flex items-center gap-2 ml-auto">
                                <button type="button" onClick={() => confirmSuggestion(m, m.pratiche_suggerite[0])} data-testid={`mail-suggest-confirm-${m.id}`} className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-2.5 py-1 transition-colors">Conferma</button>
                                <button type="button" onClick={() => openLinkDialog(m)} data-testid={`mail-suggest-manual-${m.id}`} className="rounded-md border border-slate-800/40 text-slate-900 font-medium hover:bg-amber-500 px-2.5 py-1 transition-colors">Cerca pratica manualmente</button>
                                <button type="button" onClick={() => dismissSuggestion(m)} data-testid={`mail-suggest-ignore-${m.id}`} className="rounded-md text-slate-800 font-medium hover:bg-amber-500 px-2 py-1 transition-colors">Ignora</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div className="text-xs text-slate-900 font-medium mb-2">Più pratiche corrispondono al nominativo. Seleziona quella corretta:</div>
                              <div className="space-y-1.5">
                                {m.pratiche_suggerite.map((r) => (
                                  <div key={r.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs bg-slate-900/50 border border-slate-700 rounded px-2.5 py-1.5" data-testid={`mail-suggest-row-${m.id}-${r.id}`}>
                                    <span className="font-mono text-amber-400">{r.numero_pratica}</span>
                                    <span className="text-slate-200">{r.nome}</span>
                                    {r.data_apertura ? <span className="text-slate-400">· apertura {itDate(r.data_apertura)}</span> : null}
                                    {r.data_nascita ? <span className="text-cyan-300">· nato/a il {itDate(r.data_nascita)}</span> : null}
                                    <button type="button" onClick={() => confirmSuggestion(m, r)} data-testid={`mail-suggest-pick-${m.id}-${r.id}`} className="ml-auto rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-2.5 py-1 transition-colors">Collega</button>
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                <button type="button" onClick={() => openLinkDialog(m)} data-testid={`mail-suggest-manual-${m.id}`} className="rounded-md border border-slate-800/40 text-slate-900 font-medium hover:bg-amber-500 px-2.5 py-1 text-xs transition-colors">Cerca pratica manualmente</button>
                                <button type="button" onClick={() => dismissSuggestion(m)} data-testid={`mail-suggest-ignore-${m.id}`} className="rounded-md text-slate-800 font-medium hover:bg-amber-500 px-2 py-1 text-xs transition-colors">Ignora</button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <ComposeDialog open={composeOpen} onClose={() => setComposeOpen(false)} account={account} initial={composeInit}
        onSent={(fld) => { if (fld) { setFolder(fld); load(fld); } }} />

      <Dialog open={!!linkMsg} onOpenChange={(o) => { if (!o) setLinkMsg(null); }}>
        <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-lg" data-testid="mail-link-pratica-dialog">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><FolderKanban className="h-4 w-4 text-amber-400" /> {linkMsg?.bulk ? `Collega ${linkMsg.bulk.length} messaggi a pratica` : linkMsg?.pratica_numero ? `Cambia pratica (ora: ${linkMsg.pratica_numero})` : "Collega a pratica"}</DialogTitle>
          </DialogHeader>
          {linkStep === "pratica" ? (
            <>
              <div className="py-1">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <input value={linkQuery} onChange={(e) => searchLinkPratica(e.target.value)} onFocus={() => linkResults.length && setLinkOpen(true)}
                    autoFocus autoComplete="off" placeholder="Cerca per n° pratica o nome/cognome…" data-testid="mail-link-search"
                    className="h-10 w-full rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm pl-9 pr-3 focus:outline-none focus:ring-1 focus:ring-amber-500/40" />
                </div>
                {linkOpen && (
                  <div className="mt-2 rounded-md border border-slate-700 bg-[#0d111a] max-h-64 overflow-y-auto" data-testid="mail-link-results">
                    {linkResults.length === 0 ? (
                      <div className="px-3 py-5 text-sm text-slate-500 text-center">Nessuna pratica trovata</div>
                    ) : linkResults.map((r) => {
                      const homonym = linkResults.filter((x) => normName(x.nome_assistito) === normName(r.nome_assistito)).length > 1;
                      return (
                        <button key={r.id} type="button" onClick={() => confirmLinkPratica(r)} disabled={linkSaving} data-testid={`mail-link-opt-${r.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-slate-800/60 border-b border-slate-800 last:border-0 disabled:opacity-50 transition-colors">
                          <span className="font-mono text-amber-400 text-sm">{r.numero_pratica}</span>
                          {r.nome_assistito ? <span className="text-slate-200 text-sm"> · {r.nome_assistito}</span> : ""}
                          {r.targa ? <span className="text-slate-400 text-xs font-mono ml-1.5">· {r.targa}</span> : ""}
                          {homonym && r.data_nascita ? <span className="text-[11px] text-cyan-300 ml-1.5">(nato/a il {itDate(r.data_nascita)})</span> : ""}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex justify-end pt-2">
                <button type="button" onClick={() => setLinkMsg(null)} data-testid="mail-link-cancel"
                  className="text-sm rounded-md border border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700 px-3 py-1.5 transition-colors">Annulla</button>
              </div>
            </>
          ) : (
            <>
              <div className="py-1" data-testid="mail-link-soggetto-step">
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 mb-3 text-sm">
                  <span className="font-mono text-amber-400">{linkPratica?.numero_pratica}</span>
                  {linkPratica?.nome_assistito ? <span className="text-slate-200"> · {linkPratica.nome_assistito}</span> : ""}
                </div>
                <div className="text-xs text-slate-400 mb-2">Questa pratica ha più soggetti. Scegli a quale collegare l'email:</div>
                <div className="rounded-md border border-slate-700 bg-[#0d111a] max-h-72 overflow-y-auto" data-testid="mail-link-soggetti">
                  {linkSoggetti.map((s) => (
                    <button key={s.key} type="button" onClick={() => confirmLinkSoggetto(s)} disabled={linkSaving} data-testid={`mail-link-soggetto-${s.key}`}
                      className="w-full text-left px-3 py-2.5 hover:bg-slate-800/60 border-b border-slate-800 last:border-0 disabled:opacity-50 transition-colors flex items-center gap-2">
                      <span className={`shrink-0 text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${s.tipo === "controparte" ? "bg-rose-500/15 text-rose-300" : "bg-emerald-500/15 text-emerald-300"}`}>{s.tipo_label}</span>
                      <span className="min-w-0 flex-1">
                        <span className="text-slate-100 text-sm block truncate">{s.nome}</span>
                        {s.ruolo ? <span className="text-[11px] text-slate-400">{s.ruolo}</span> : null}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-between pt-2">
                <button type="button" onClick={() => { setLinkStep("pratica"); setLinkPratica(null); setLinkSoggetti([]); setLinkOpen(true); }} data-testid="mail-link-soggetto-back"
                  className="text-sm rounded-md border border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700 px-3 py-1.5 transition-colors">← Indietro</button>
                <button type="button" onClick={() => setLinkMsg(null)} data-testid="mail-link-soggetto-cancel"
                  className="text-sm rounded-md border border-slate-700 bg-slate-800/60 text-slate-200 hover:bg-slate-700 px-3 py-1.5 transition-colors">Annulla</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <MailAttToPraticaDialog msg={attMsg} account={account} onClose={() => setAttMsg(null)} />
      <AlertDialog open={confirmBulk} onOpenChange={(o) => !bulkBusy && setConfirmBulk(o)}>
        <AlertDialogContent data-testid="mail-bulk-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {checked.length} {checked.length === 1 ? "messaggio" : "messaggi"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {folder === "trash" ? "I messaggi selezionati verranno eliminati definitivamente. L'operazione non è reversibile." : "I messaggi selezionati verranno spostati nel Cestino."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy} data-testid="mail-bulk-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); bulkDelete(); }} disabled={bulkBusy} data-testid="mail-bulk-confirm-ok" className="bg-red-600 hover:bg-red-700 text-white">
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
